import React, { useState } from 'react';
import { gql, useQuery } from '@common/hooks';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  AlignedInfo,
  AddButton,
  Icon,
  Animation,
} from '@common/components';
import {
  Affix,
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
} from 'antd';
import { Colors } from 'works/legacy/lib/style-helper';
import { humanizeTimezone } from 'works/legacy/lib/date-helper';
import {
  FormFieldUnitName,
  FormFieldClinicalUnit,
} from '@institutions/components';
import {
  ClinicalUnitType,
  OrganizationBusinessType,
} from '@institutions/types';

const AddUnitsInsertDetailsStepQuery = gql`
  query AddUnitsInsertDetailsStepQuery(
    $id: ID!
    $organizationUnitsInput: OrganizationUnitsInput
  ) {
    worksClinicalUnits {
      id
      displayName
    }
    organizationBusiness(id: $id) {
      id
      name
      address
      postalCode
      city
      country
      stateCode
      timeZone
      organizationUnits(input: $organizationUnitsInput) {
        id
        name
      }
    }
  }
`;

type OrganizationUnitType = { id: string; name: string };

type AddUnitsInsertDetailsStepQueryResponse = {
  worksClinicalUnits: ClinicalUnitType[];
  organizationBusiness: OrganizationBusinessType & {
    organizationUnits: OrganizationUnitType[];
  };
};

type AddUnitsInsertDetailsStepQueryVariables = {
  id: string;
  organizationUnitsInput: { scope: string };
};

type FormUnit = {
  unitName: string;
  clinicalUnitId: string;
  costCenter: string;
};

export type AddUnitsDetailsStepFormValuesType = {
  units: FormUnit[];
};

type Props = {
  unitsForm: FormInstance;
  selectedFacilityId?: string;
  onFormFinish: (values: AddUnitsDetailsStepFormValuesType) => void;
};

export function AddUnitsInsertDetailsStep({
  unitsForm,
  selectedFacilityId = '',
  onFormFinish,
}: Props) {
  const { data, loading, error } = useQuery<
    AddUnitsInsertDetailsStepQueryResponse,
    AddUnitsInsertDetailsStepQueryVariables
  >(AddUnitsInsertDetailsStepQuery, {
    skip: !selectedFacilityId,
    variables: {
      id: selectedFacilityId,
      organizationUnitsInput: { scope: 'organization' },
    },
  });

  if (error) {
    return <ErrorHandler error={error} log="AddUnitsInsertDetailsStep" />;
  }

  if (loading) {
    return (
      <AppContent>
        <LoadingState />
      </AppContent>
    );
  }

  const facility = data?.organizationBusiness;

  const getAllUnitNames = () =>
    unitsForm.getFieldsValue()?.units?.map((unit: FormUnit) => unit.unitName) ||
    [];

  return (
    <>
      <Form
        form={unitsForm}
        layout="vertical"
        className="add-units-insert-details-step"
        onFinish={onFormFinish}
      >
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Affix offsetTop={150}>
              <Form.Item label="Facility">
                <Select
                  disabled
                  style={{ maxWidth: 320 }}
                  placeholder="Loading..."
                  value={selectedFacilityId}
                  options={[
                    {
                      value: data?.organizationBusiness.id,
                      label: data?.organizationBusiness.name,
                    },
                  ]}
                />
              </Form.Item>
              <AlignedInfo
                content={[
                  ['Address', facility?.address],
                  ['ZIP Code', facility?.postalCode],
                  ['City', facility?.city],
                  ['State', facility?.stateCode],
                  ['Time Zone', humanizeTimezone(facility?.timeZone)],
                ]}
              />
            </Affix>
          </Col>
          <Col span={12}>
            <Form.List
              name="units"
              initialValue={[
                {
                  unitName: '',
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name: index }) => (
                    <SingleUnitForm
                      key={key}
                      index={index}
                      showDelete={fields.length > 1}
                      onRemove={() => remove(index)}
                      organizationUnits={
                        data?.organizationBusiness.organizationUnits || []
                      }
                      clinicalUnits={data?.worksClinicalUnits || []}
                      getAllUnitNames={getAllUnitNames}
                    />
                  ))}
                  <div className="add-units-insert-details-step__footer color-midnight-lighter">
                    <AddButton
                      type="link"
                      style={{ paddingLeft: 0 }}
                      onClick={add}
                    >
                      Unit
                    </AddButton>
                    <div>Total: {fields.length}</div>
                  </div>
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
      <style jsx>{`
        .add-units-insert-details-step__footer {
          display: flex;
          justify-content: space-between;
          line-height: 32px;
        }
      `}</style>
    </>
  );
}

function SingleUnitForm({
  index,
  showDelete,
  clinicalUnits,
  organizationUnits,
  onRemove,
  getAllUnitNames,
}: {
  index: number;
  showDelete: boolean;
  clinicalUnits: ClinicalUnitType[];
  organizationUnits: OrganizationUnitType[];
  onRemove: () => void;
  getAllUnitNames: () => string[];
}) {
  const [animationActive, setAnimationActive] = useState(false);

  return (
    <>
      <Animation
        kind={['shrink-vertical', 'fade-out']}
        active={animationActive}
        onAnimationEnd={onRemove}
      >
        <div className="single-unit-form-fields">
          {showDelete && (
            <Button
              type="link"
              className="single-unit-form-fields__delete"
              onClick={() => setAnimationActive(true)}
            >
              <Icon icon="trashCan" />
            </Button>
          )}
          <div className="single-unit-form-fields__title">
            Unit #{index + 1}
          </div>
          <FormFieldUnitName
            fieldName={[index, 'unitName']}
            organizationUnits={organizationUnits}
            getAllUnitNames={getAllUnitNames}
          />
          <FormFieldClinicalUnit
            fieldName={[index, 'clinicalUnitId']}
            clinicalUnits={clinicalUnits}
          />
          <Form.Item label="Cost Center" name={[index, 'costCenter']}>
            <Input placeholder="Type the Cost Center" />
          </Form.Item>
        </div>
      </Animation>
      <style jsx>{`
        .single-unit-form-fields {
          position: relative;
          border: solid 1px ${Colors.steel};
          border-radius: 8px;
          margin-bottom: 24px;
          padding: 24px;
        }
        .single-unit-form-fields__title {
          font-weight: 800;
          margin-bottom: 12px;
        }
        .single-unit-form-fields :global(.single-unit-form-fields__delete) {
          position: absolute;
          right: 8px;
          top: 16px;
        }
      `}</style>
    </>
  );
}
